import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquareIcon, Loader2Icon, ImageIcon } from 'lucide-react'
import { fetchPropertyJobChats, type PropertyJobChatItem } from '../../api/teamChat'

interface PropertyJobChatsPanelProps {
  propertyId: string
}

export function PropertyJobChatsPanel({ propertyId }: PropertyJobChatsPanelProps) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<PropertyJobChatItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await fetchPropertyJobChats(propertyId)
        if (!cancelled) setRows(data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load job chats')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [propertyId])

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>
  }

  return (
    <div className="bg-indigo-50 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquareIcon className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold text-gray-900">Job chats ({rows.length})</h2>
      </div>
      {rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-indigo-200 p-8 text-center">
          <MessageSquareIcon className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
          <p className="text-gray-500">No jobs for this property yet</p>
        </div>
      ) : (
        <>
          <ul className="space-y-2 lg:hidden">
            {rows.map((row) => (
              <li
                key={row.job_id}
                className="rounded-xl border border-indigo-100 bg-white p-3 space-y-1.5"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/details/${row.job_id}/chat`)}
                  className="text-sm font-semibold text-blue-600 text-left"
                >
                  {row.job_number ? `#${row.job_number}` : 'Job'} · {row.title || 'Untitled'}
                </button>
                <p className="text-xs text-gray-500">{row.status || 'N/A'}</p>
                <p className="text-sm text-gray-700 line-clamp-2">
                  {row.has_attachment && (
                    <ImageIcon className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
                  )}
                  {row.last_message_preview ||
                    (row.channel_id ? 'No messages yet' : 'Chat not started')}
                </p>
                {row.last_message_at && (
                  <p className="text-xs text-gray-400">
                    {new Date(row.last_message_at).toLocaleString()}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-indigo-100 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={row.job_id} className="hover:bg-indigo-50/30">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">
                        {row.job_number ? `#${row.job_number}` : 'N/A'}
                      </div>
                      <div className="text-gray-600">{row.title || 'Untitled'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{row.status || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div className="line-clamp-2">
                        {row.has_attachment && (
                          <ImageIcon className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
                        )}
                        {row.last_message_preview ||
                          (row.channel_id ? 'No messages yet' : 'Chat not started')}
                      </div>
                      {row.last_message_at && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {new Date(row.last_message_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        type="button"
                        onClick={() => navigate(`/jobs/details/${row.job_id}/chat`)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Open chat
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
